import type { ReactNode } from 'react';
import {
  MonthlyTrendingComparativeViewValues,
  type MonthlyTrendingComparativeView,
  type ReportLineType,
} from '@erp/shared-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { T } from 'gt-react';

type Option<T extends string> = { id: string; label: string; value: T };

function LineSelect<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: ReactNode;
  value: T;
  options: Array<Option<T>>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={id} className="text-sm text-foreground whitespace-nowrap">
        {label}
      </Label>
      <Select value={value} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id} className="w-[130px] h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.id} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function MonthlyTrendingComparativeViewToggle(p: {
  value: MonthlyTrendingComparativeView;
  onChange: (value: MonthlyTrendingComparativeView) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm">
      <Switch
        checked={p.value === MonthlyTrendingComparativeViewValues.REPORTED}
        onCheckedChange={(checked) =>
          p.onChange(
            checked
              ? MonthlyTrendingComparativeViewValues.REPORTED
              : MonthlyTrendingComparativeViewValues.CURRENT,
          )
        }
      />
      <span>
        <T context="switch label, show comparative YTD using frozen reported categories">
          Show Reported
        </T>
      </span>
    </label>
  );
}

export function MonthlyTrendingLineSelects(p: {
  priorYear: ReportLineType;
  priorYearOptions: Array<Option<ReportLineType>>;
  onPriorYearChange: (value: ReportLineType) => void;
  throughMonth: number;
  monthNames: string[];
  onThroughMonthChange: (value: number) => void;
}) {
  return (
    <>
      <LineSelect
        id="prior-year-data"
        label={
          <T context="filter label, prior year comparison data type">
            Prior Year Data
          </T>
        }
        value={p.priorYear}
        options={p.priorYearOptions}
        onChange={p.onPriorYearChange}
      />
      <LineSelect
        id="through-month"
        label={
          <T context="label for month-through cutoff selector on rebate report">
            Through:
          </T>
        }
        value={String(p.throughMonth)}
        options={p.monthNames.map((label, i) => ({
          id: String(i + 1),
          label,
          value: String(i + 1),
        }))}
        onChange={(value) => p.onThroughMonthChange(Number(value))}
      />
    </>
  );
}
